import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { 
  Trophy, 
  Clock, 
  CheckCircle2, 
  Circle, 
  Edit, 
  Award,
  Target,
  TrendingUp,
  Calendar,
  User,
  ChevronRight
} from 'lucide-react';
import { format } from 'date-fns';

const StudentProgress = () => {
  const [students, setStudents] = useState([]);
  const [progressData, setProgressData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    layer_one_complete: false,
    layer_two_complete: false,
    cube_complete: false,
    best_time_seconds: '',
    notes: ''
  });
  const user = JSON.parse(localStorage.getItem('user'));
  const isTeacher = user?.role === 'teacher';
  const isAdmin = user?.role === 'admin';
  const isStudent = user?.role === 'student';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (isStudent) {
        // Student views their own progress
        const progressResponse = await axios.get(`${API}/student-progress/${user.id}`, { headers });
        setProgressData({ [user.id]: progressResponse.data });
        setStudents([user]);
      } else {
        // Teachers and admins view all students
        const studentsResponse = await axios.get(`${API}/users?role=student`, { headers });
        setStudents(studentsResponse.data);

        const progressResponse = await axios.get(`${API}/student-progress`, { headers });
        const progressMap = {};
        progressResponse.data.forEach(progress => {
          if (!progressMap[progress.student_id]) {
            progressMap[progress.student_id] = [];
          }
          progressMap[progress.student_id].push(progress);
        });
        setProgressData(progressMap);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load progress data');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (student) => {
    setSelectedStudent(student);
    const progress = progressData[student.id]?.[0]; // Get Course 1 progress
    
    if (progress) {
      setFormData({
        layer_one_complete: progress.milestones?.layer_one_complete || false,
        layer_two_complete: progress.milestones?.layer_two_complete || false,
        cube_complete: progress.milestones?.cube_complete || false,
        best_time_seconds: progress.best_time_seconds || '',
        notes: progress.notes || ''
      });
    } else {
      setFormData({
        layer_one_complete: false,
        layer_two_complete: false,
        cube_complete: false,
        best_time_seconds: '',
        notes: ''
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/student-progress?student_id=${selectedStudent.id}`,
        {
          course_name: 'Course 1',
          ...formData,
          best_time_seconds: formData.best_time_seconds ? parseInt(formData.best_time_seconds) : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Progress updated successfully');
      setIsDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error updating progress:', error);
      toast.error('Failed to update progress');
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return 'Not recorded';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const getProgressPercentage = (progress) => {
    if (!progress) return 0;
    const milestones = progress.milestones || {};
    const completed = [
      milestones.layer_one_complete,
      milestones.layer_two_complete,
      milestones.cube_complete
    ].filter(Boolean).length;
    return Math.round((completed / 3) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {isStudent ? 'My Progress' : 'Student Progress'}
          </h2>
          <p className="text-gray-600 text-sm mt-1">
            {isStudent ? 'Track your course completion' : 'Monitor student course milestones and best times'}
          </p>
        </div>
      </div>

      {/* Progress Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {students.map(student => {
          const progress = progressData[student.id]?.[0];
          const percentage = getProgressPercentage(progress);
          const milestones = progress?.milestones || {};

          return (
            <Card key={student.id} className="p-6 hover:shadow-lg transition-all">
              <div className="space-y-4">
                {/* Student Info */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{student.name}</h3>
                      <p className="text-sm text-gray-600">{student.email}</p>
                    </div>
                  </div>
                  {(isTeacher || isAdmin) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(student)}
                      className="shrink-0"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {/* Progress Bar */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Overall Progress</span>
                    <span className="text-sm font-bold text-blue-600">{percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>

                {/* Milestones */}
                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-900 text-sm">Course 1 Milestones</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      {milestones.layer_one_complete ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : (
                        <Circle className="w-5 h-5 text-gray-300" />
                      )}
                      <span className={milestones.layer_one_complete ? 'text-gray-900' : 'text-gray-500'}>
                        Layer One
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      {milestones.layer_two_complete ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : (
                        <Circle className="w-5 h-5 text-gray-300" />
                      )}
                      <span className={milestones.layer_two_complete ? 'text-gray-900' : 'text-gray-500'}>
                        Layer Two
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      {milestones.cube_complete ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : (
                        <Circle className="w-5 h-5 text-gray-300" />
                      )}
                      <span className={milestones.cube_complete ? 'text-gray-900' : 'text-gray-500'}>
                        Cube Complete
                      </span>
                    </div>
                  </div>
                </div>

                {/* Best Time */}
                <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-4 rounded-lg border border-yellow-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-yellow-600" />
                      <span className="text-sm font-medium text-gray-700">Best Time</span>
                    </div>
                    <span className="text-lg font-bold text-yellow-700">
                      {formatTime(progress?.best_time_seconds)}
                    </span>
                  </div>
                </div>

                {/* Notes */}
                {progress?.notes && (
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                    <p className="text-sm text-gray-700">{progress.notes}</p>
                  </div>
                )}

                {/* Last Updated */}
                {progress?.updated_at && (
                  <div className="text-xs text-gray-500 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Updated {format(new Date(progress.updated_at), 'MMM dd, yyyy')}
                    {progress.updated_by_name && ` by ${progress.updated_by_name}`}
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Edit Progress Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              Update Progress - {selectedStudent?.name}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Milestones */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Course 1 Milestones</h3>
              
              <div className="flex items-center space-x-2 p-4 border rounded-lg hover:bg-gray-50">
                <Checkbox
                  id="layer_one"
                  checked={formData.layer_one_complete}
                  onCheckedChange={(checked) => setFormData({ ...formData, layer_one_complete: checked })}
                />
                <Label htmlFor="layer_one" className="cursor-pointer flex-1">
                  <div className="font-medium">Layer One Complete</div>
                  <div className="text-sm text-gray-600">Student has mastered the first layer</div>
                </Label>
              </div>

              <div className="flex items-center space-x-2 p-4 border rounded-lg hover:bg-gray-50">
                <Checkbox
                  id="layer_two"
                  checked={formData.layer_two_complete}
                  onCheckedChange={(checked) => setFormData({ ...formData, layer_two_complete: checked })}
                />
                <Label htmlFor="layer_two" className="cursor-pointer flex-1">
                  <div className="font-medium">Layer Two Complete</div>
                  <div className="text-sm text-gray-600">Student has mastered the second layer</div>
                </Label>
              </div>

              <div className="flex items-center space-x-2 p-4 border rounded-lg hover:bg-gray-50">
                <Checkbox
                  id="cube_complete"
                  checked={formData.cube_complete}
                  onCheckedChange={(checked) => setFormData({ ...formData, cube_complete: checked })}
                />
                <Label htmlFor="cube_complete" className="cursor-pointer flex-1">
                  <div className="font-medium">Cube Complete</div>
                  <div className="text-sm text-gray-600">Student can solve the entire cube</div>
                </Label>
              </div>
            </div>

            {/* Best Time */}
            <div className="space-y-2">
              <Label htmlFor="best_time">Best Time (in seconds)</Label>
              <div className="flex gap-2 items-center">
                <Input
                  id="best_time"
                  type="number"
                  placeholder="Enter time in seconds (e.g., 120 for 2 minutes)"
                  value={formData.best_time_seconds}
                  onChange={(e) => setFormData({ ...formData, best_time_seconds: e.target.value })}
                  min="1"
                />
                <Clock className="w-5 h-5 text-gray-400" />
              </div>
              <p className="text-sm text-gray-600">
                Current best: {formatTime(progressData[selectedStudent?.id]?.[0]?.best_time_seconds)}
              </p>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Add any observations or comments..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={4}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                Save Progress
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentProgress;
